import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../auth/supabaseClient';

/**
 * Capture-side magic-link invite acceptance.
 *
 * This is the Capture-origin port of Library's src/pages/AcceptInvitePage.tsx.
 * It exists because Supabase sessions are localStorage-scoped PER ORIGIN: an
 * invite created from capture.steliad.com must be accepted AND authenticated on
 * capture.steliad.com, or the freshly-minted session would live on the wrong
 * origin (app.steliad.com) and the user would land in Library, unauthenticated
 * for Capture. So the whole accept/OTP round-trip completes here.
 *
 * Flow (identical to Library's):
 *   1. Page loads with ?token=...
 *   2. Look up the invite (unauthenticated) to learn which email the OTP goes to.
 *   3a. If a Supabase session already exists → POST accept directly, land on '/'.
 *   3b. If no session → show the invite email (read-only) + "Continue with email"
 *       which calls signInWithOtp. emailRedirectTo is built from
 *       window.location.origin, so it stays CAPTURE-relative — the magic link
 *       returns the user to THIS page on THIS origin with a session, and 3a fires.
 *
 * Backend calls go through the same-origin `/api/library/*` proxy (capture-ui
 * setupProxy → Library `/api/*`), so no browser CORS is involved: the browser
 * only ever talks to the Capture origin.
 *
 * Reuses capture-ui's existing Supabase client (auth/supabaseClient). Direct
 * supabase.auth use here matches sibling pages (ReviewDetailPage,
 * SimplePDFViewer) — this is an auth screen.
 *
 * TODO(cross-subdomain SSO): today a user signed into Library still has to
 * re-auth for Capture (and vice-versa) because the Supabase session lives in
 * per-origin localStorage. True single-sign-on across *.steliad.com requires
 * moving Supabase session storage to a parent-domain (.steliad.com) cookie
 * (custom storage adapter writing a Domain=.steliad.com cookie). Deliberately
 * out of scope here — this route only fixes the accept-origin landing.
 */

// Same-origin Library proxy base (capture-ui setupProxy rewrites → Library /api).
const LIBRARY_BASE = process.env.REACT_APP_LIBRARY_BASE_PATH || '/api/library';

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const query = useQuery();
  const token = (query.get('token') || '').trim();

  const [phase, setPhase] = useState('loading'); // loading|sign_in|sending_otp|otp_sent|accepting|done|error
  const [error, setError] = useState(null);
  const [invite, setInvite] = useState(null); // { email, tenantName, role, expiresAt }

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setError(null);

      if (!token) {
        setPhase('error');
        setError('Missing invite token.');
        return;
      }

      // Look up invite metadata first — needed to show the email in the sign-in
      // form AND to surface a helpful error (expired/revoked) before sign-in.
      // /invites/lookup is on Library's tenant-agnostic allowlist (no session).
      let lookup = null;
      try {
        const res = await fetch(
          `${LIBRARY_BASE}/invites/lookup?token=${encodeURIComponent(token)}`,
          { method: 'GET' }
        );
        const body = await res.json().catch(() => null);
        if (!res.ok) {
          throw new Error((body && body.error) || `Lookup failed (${res.status})`);
        }
        lookup = body;
        if (!cancelled) setInvite(lookup);
      } catch (err) {
        if (!cancelled) {
          setPhase('error');
          setError(String(err?.message || err));
        }
        return;
      }

      // If a Supabase session already exists, accept the invite right away.
      const { data } = await supabase.auth.getSession();
      const session = data?.session || null;

      if (session) {
        try {
          if (!cancelled) setPhase('accepting');
          const res = await fetch(`${LIBRARY_BASE}/invites/accept`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ token }),
          });
          const body = await res.json().catch(() => null);
          if (!res.ok) {
            throw new Error((body && body.error) || `Accept failed (${res.status})`);
          }
          if (!cancelled) {
            setPhase('done');
            // Land in Capture. This origin now holds the session.
            navigate('/', { replace: true });
          }
        } catch (err) {
          if (!cancelled) {
            setPhase('error');
            setError(String(err?.message || err));
          }
        }
        return;
      }

      // No session — show the magic-link sign-in form.
      if (!cancelled) setPhase('sign_in');
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, token]);

  const handleContinue = async () => {
    if (!invite?.email || !token) return;

    setError(null);
    setPhase('sending_otp');

    // Origin-relative → stays on the Capture origin so the session is minted here.
    const redirectTo = `${window.location.origin}/invites/accept?token=${encodeURIComponent(token)}`;

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: invite.email,
        options: {
          emailRedirectTo: redirectTo,
          shouldCreateUser: true,
        },
      });

      if (otpError) {
        setPhase('error');
        setError(otpError.message);
        return;
      }

      setPhase('otp_sent');
    } catch (err) {
      setPhase('error');
      setError(String(err?.message || err));
    }
  };

  return (
    <div style={styles.scroll}>
      <div style={styles.wrapper}>
        <div style={styles.card}>
          <div style={styles.brand}>
            <img src="/steliad_logo_navy.png" alt="Steliad" style={styles.logo} />
            <span style={styles.productName}>Capture</span>
          </div>

          <h1 style={styles.title}>Accept invite</h1>

          {phase === 'loading' && <div style={styles.muted}>Checking your invite…</div>}

          {phase === 'accepting' && <div style={styles.muted}>Accepting invite…</div>}

          {phase === 'done' && <div style={styles.muted}>Invite accepted. Redirecting…</div>}

          {phase === 'sign_in' && invite && (
            <>
              {invite.tenantName ? (
                <p style={styles.lead}>
                  You've been invited to join <strong>{invite.tenantName}</strong>.
                </p>
              ) : (
                <p style={styles.lead}>You've been invited to a workspace.</p>
              )}

              <p style={styles.muted}>
                We'll send a one-time sign-in link to the address below. No password needed.
              </p>

              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={invite.email}
                readOnly
                style={styles.input}
                aria-label="Invite email"
              />

              <button type="button" onClick={handleContinue} style={styles.button}>
                Continue with email
              </button>

              <p style={styles.fine}>
                The link will only work for <strong>{invite.email}</strong>. If this isn't
                your address, ask the inviter to re-send to the correct email.
              </p>
            </>
          )}

          {phase === 'sending_otp' && <div style={styles.muted}>Sending sign-in link…</div>}

          {phase === 'otp_sent' && invite && (
            <>
              <p style={styles.lead}>Check your email.</p>
              <p style={styles.muted}>
                We just sent a one-time sign-in link to <strong>{invite.email}</strong>.
                Click the link in that email to finish accepting this invite.
              </p>
              <p style={styles.fine}>
                You can close this tab — the link will bring you back here automatically.
              </p>
            </>
          )}

          {phase === 'error' && (
            <div style={styles.errorBox}>
              <strong>Could not accept this invite.</strong>
              <div style={{ marginTop: 6 }}>{error}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  // Scrollable-auth pattern: own the full viewport and scroll internally so the
  // card is never clipped by an ancestor's overflow (this route renders outside
  // the app chrome, like /login).
  scroll: {
    minHeight: '100vh',
    overflowY: 'auto',
    background: '#f1f5f9',
  },
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem 1rem',
    boxSizing: 'border-box',
  },
  card: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
    padding: '2rem',
    width: '100%',
    maxWidth: 480,
  },
  brand: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  logo: { height: 48, width: 'auto' },
  productName: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#94a3b8',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  title: {
    margin: 0,
    marginBottom: '1rem',
    fontSize: '1.5rem',
    color: '#0f172a',
    textAlign: 'center',
  },
  lead: { fontSize: 15, color: '#0f172a', marginTop: 0, marginBottom: '1rem' },
  muted: { fontSize: 14, color: '#475569', marginTop: 0, marginBottom: '1rem' },
  fine: { fontSize: 12, color: '#64748b', marginTop: '1rem', marginBottom: 0 },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#334155',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.625rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    fontSize: 14,
    color: '#0f172a',
    background: '#f8fafc',
    marginBottom: '1rem',
  },
  button: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.75rem 1rem',
    // Steliad CTA tokens (gold with navy text), matching Login.js's primary
    // button — was generic blue #2563eb.
    background: 'var(--stm-accent-primary)',
    color: 'var(--stm-text-on-accent)',
    border: 'none',
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    padding: '0.75rem 1rem',
    borderRadius: 6,
    fontSize: 14,
  },
};
